import { Map, MapControls, MapMarker, MarkerContent, useMap } from "@repo/ui/components/map";
import { useEffect, useMemo, useRef } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_CENTER = [15.985, 60.279];
const DEFAULT_ZOOM = 13;
const CARTO_STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";
export function PinPicker({
  latitude,
  longitude,
  zoom,
  onPick,
  placeMarker
}) {
  // Lock initial values; map only initializes once
  const initial = useRef({
    latitude,
    longitude,
    zoom,
    placeMarker
  });
  const {
    center,
    initZoom,
    maxBounds
  } = useMemo(() => {
    const {
      latitude: lat,
      longitude: lng,
      zoom: z,
      placeMarker: pm
    } = initial.current;
    return {
      center: lng && lat ? [lng, lat] : DEFAULT_CENTER,
      initZoom: lat ? z ?? 15 : DEFAULT_ZOOM,
      maxBounds: pm ? [[pm.longitude - 0.006, pm.latitude - 0.004], [pm.longitude + 0.006, pm.latitude + 0.004]] : undefined
    };
  }, []);
  const styles = useMemo(() => ({
    light: CARTO_STYLE
  }), []);
  return /*#__PURE__*/_jsxs("div", {
    className: "relative h-full w-full",
    children: [/*#__PURE__*/_jsxs(Map, {
      styles: styles,
      center: center,
      zoom: initZoom,
      maxBounds: maxBounds,
      children: [/*#__PURE__*/_jsx(MapControls, {
        position: "top-right",
        showZoom: true
      }), placeMarker && /*#__PURE__*/_jsx(MapMarker, {
        longitude: placeMarker.longitude,
        latitude: placeMarker.latitude,
        children: /*#__PURE__*/_jsx(MarkerContent, {
          children: /*#__PURE__*/_jsx("div", {
            className: "h-3 w-3 rounded-full border-2 border-white bg-blue-500 shadow"
          })
        })
      }), /*#__PURE__*/_jsx(CenterEmitter, {
        onPick: onPick,
        autoLocateZoom: initial.current.zoom ?? 18,
        autoLocate: !initial.current.latitude && !initial.current.longitude,
        initialLat: initial.current.latitude,
        initialLng: initial.current.longitude
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: "pointer-events-none absolute inset-0 flex items-center justify-center",
      children: /*#__PURE__*/_jsxs("svg", {
        width: "32",
        height: "42",
        viewBox: "0 0 32 42",
        fill: "none",
        className: "drop-shadow-md",
        children: [/*#__PURE__*/_jsx("path", {
          d: "M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z",
          fill: "#f59e0b"
        }), /*#__PURE__*/_jsx("circle", {
          cx: "16",
          cy: "16",
          r: "6",
          fill: "white"
        })]
      })
    })]
  });
}
function CenterEmitter({
  onPick,
  autoLocate,
  autoLocateZoom,
  initialLat,
  initialLng
}) {
  const {
    map,
    isLoaded
  } = useMap();
  const cbRef = useRef(onPick);
  cbRef.current = onPick;

  // Emit initial coords if provided
  useEffect(() => {
    if (initialLat && initialLng) {
      cbRef.current(initialLat, initialLng);
    }
  }, [initialLat, initialLng]);

  // Emit on every moveend
  useEffect(() => {
    if (!isLoaded || !map) return;
    const emit = () => {
      const c = map.getCenter();
      cbRef.current(c.lat, c.lng);
    };
    map.on("moveend", emit);
    return () => {
      map.off("moveend", emit);
    };
  }, [isLoaded, map]);

  // Auto-locate user when no initial coords
  useEffect(() => {
    if (!isLoaded || !map || !autoLocate) return;
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(pos => {
      map.flyTo({
        center: [pos.coords.longitude, pos.coords.latitude],
        zoom: autoLocateZoom,
        duration: 500
      });
    }, () => {
      // Silent fail — user keeps default center
    });
  }, [isLoaded, map, autoLocate, autoLocateZoom]);
  return null;
}